import './chosen-bank-info.scss';

import { useContext, useMemo, useState } from 'react';

import { CustomModal } from '@/components/Shared/Modal/CustomModal';
import {
  Button,
  ChosenBankStatus,
  DocumentController
} from '@/components/Shared/ui';
import { BANKS_ICONS_NAMES, BankNames, DictTypes } from '@/constants';
import CrossIcon from '@/media/icons/cross.svg?react';
import InfoCircleIcon from '@/media/icons/info-circle.svg?react';
import warningIcon from '@/media/icons/warning-fill.png';
import { checkIsValidJSON } from '@/services/helpers/checkIsValidJSON';
import { safeKey } from '@/services/helpers/dict.ts';
import { useAppSelector } from '@/store';

import { UpdateStatusContext } from '../../../entities';
import { EU_3_STEP_STATUSES } from '../../../entities/update-status-modal/update-status-modal';
import { CancelModal, RefundModal } from '../../entities';
import { BankExternalQr } from './features';

const hideCancelStatuses = [
  'financed',
  'cancelled',
  'refund_in_progress',
  'refunded'
];

const paymentOrderFileTypeByBank: Partial<Record<BankNames, string>> = {
  altyn: 'paymentOrderAltyn',
  bcc: 'paymentOrderBcc',
  bereke: 'BerekePaymentOrder',
  ffb: 'payment_order',
  forte: 'PaymentOrderForte',
  halyk: 'PaymentOrderHalyk',
  solva: 'PaymentOrderSolva'
};

const ChosenBankInfo: React.FC = () => {
  const { onUpdateOfferStatus } = useContext(UpdateStatusContext);

  const {
    userAccesses: {
      global: { isAdmin },
      autoCredit: { canChangeChosenBankStatus, canRefundOffer, canCancelOffer }
    }
  } = useAppSelector((state) => state.userInfo);
  const { selectedBank, selectedLeasing, attachedFiles } = useAppSelector(
    (state) => state.financingStep
  );
  const { dicts } = useAppSelector((state) => state.dict);

  const bank =
    dicts?.[DictTypes.Banks]?.getBy.name[safeKey(selectedBank?.bank.name)];
  const banIconName =
    BANKS_ICONS_NAMES[
      (selectedBank?.bank.name || '') as keyof typeof BANKS_ICONS_NAMES
    ];

  const [description, setDescription] = useState('');
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showRefundModal, setShowRefundModal] = useState<boolean>(false);

  const handleOpenModal = () => {
    const description = selectedBank?.bankDescription;
    if (!description) {
      setDescription('Описание недоступно.');
      return;
    }

    if (checkIsValidJSON(description)) {
      const parsedDescription = JSON.parse(description);
      if (parsedDescription.status_code) {
        setDescription(parsedDescription.status_code);
      } else if (parsedDescription.stateTitle) {
        setDescription(parsedDescription.stateTitle);
      } else {
        setDescription(JSON.stringify(parsedDescription, null, 2));
      }
    } else {
      setDescription(description);
    }
  };

  const isEuBankV2 = selectedBank?.bank.name === 'eu_bank_v2';
  const isHasRefund = selectedBank?.status.code === 'financed' && isEuBankV2;
  const isWaitingOfFinancing =
    selectedBank?.status.code === 'waiting_of_financing';

  const isEuStatusEditable = useMemo(() => {
    if (!selectedBank || !isEuBankV2) return false;
    return EU_3_STEP_STATUSES.some(
      (euEditableStatus) => euEditableStatus === selectedBank.status.code
    );
  }, [selectedBank, isEuBankV2]);

  const paymentOrderFile: string = useMemo(() => {
    if (!selectedBank) return '';

    const paymentOrderFileType =
      paymentOrderFileTypeByBank[selectedBank.bank.name];
    if (!paymentOrderFileType) return '';

    return (
      attachedFiles.find((file) => file.type === paymentOrderFileType)?.uuid ||
      ''
    );
  }, [attachedFiles, selectedBank]);

  const computedPaymentOrderFileUrl =
    selectedLeasing?.status.code === 'financed' &&
    selectedLeasing.paymentOrderFileUrl
      ? selectedLeasing.paymentOrderFileUrl
      : null;

  const computedGuaranteeLetterFileUrl = selectedLeasing?.guaranteeLetterFileUrl
    ? selectedLeasing.guaranteeLetterFileUrl
    : null;

  return (
    <>
      <div className="chosen-bank-info">
        <div className="chosen-bank-info__header">
          <ChosenBankStatus
            img={
              bank?.icon ||
              banIconName?.icon ||
              selectedLeasing?.leasingCompany.icon ||
              warningIcon
            }
            name={
              bank?.fullname ||
              banIconName?.name ||
              selectedLeasing?.leasingCompany.name ||
              'Банк'
            }
            status={
              isAdmin && isEuBankV2
                ? {
                    code: selectedBank?.status.code || '',
                    name: selectedBank?.status.name
                  }
                : {
                    code: !isWaitingOfFinancing
                      ? selectedBank?.status.code ||
                        selectedLeasing?.status.code ||
                        ''
                      : 'chosen',
                    name: !isWaitingOfFinancing
                      ? selectedBank?.status.name ||
                        selectedLeasing?.status.name
                      : undefined
                  }
            }
            size="big"
            onEdit={
              canChangeChosenBankStatus &&
              isAdmin &&
              isEuStatusEditable &&
              isEuBankV2
                ? () => {
                    onUpdateOfferStatus(selectedBank.id, true);
                  }
                : undefined
            }
          />

          {selectedBank?.bankName === BankNames.altyn &&
            selectedBank?.altynInnerStateTitle && (
              <div className="chosen-bank-info__inner-state-title">
                <InfoCircleIcon /> {selectedBank.altynInnerStateTitle}
              </div>
            )}
        </div>

        {selectedBank?.status.code === 'financed' && !!paymentOrderFile && (
          <DocumentController
            fileLink={paymentOrderFile}
            title="Платежное поручение"
            downloadable
            wrapperClassName="chosen-bank-info__documents"
          />
        )}

        {!!computedPaymentOrderFileUrl && (
          <DocumentController
            fileLink={computedPaymentOrderFileUrl}
            title="Платежное поручение"
            oldDownloadMethod
            downloadable
            wrapperClassName="chosen-bank-info__documents"
          />
        )}

        {!!computedGuaranteeLetterFileUrl && (
          <DocumentController
            fileLink={computedGuaranteeLetterFileUrl}
            title="Гарантийное письмо"
            oldDownloadMethod
            downloadable
            wrapperClassName="chosen-bank-info__documents"
          />
        )}

        <div className="chosen-bank-info__actions">
          {(selectedBank?.bankDescription ||
            (selectedBank?.bank.name.includes('eu_bank') &&
              selectedBank?.responseApprovedBody)) && (
            <Button
              size="small"
              className="chosen-bank-info__action"
              onClick={handleOpenModal}
            >
              <InfoCircleIcon />{' '}
              <span className="chosen-bank-info__action-hidden">Описание</span>
            </Button>
          )}

          {isHasRefund && (
            <Button
              size="small"
              variant="filled-red"
              className="chosen-bank-info__action"
              onClick={() => setShowRefundModal(true)}
              disabled={!canRefundOffer}
            >
              Возврат
            </Button>
          )}

          {selectedBank &&
            !hideCancelStatuses.includes(selectedBank?.status.code || '') && (
              <Button
                size="small"
                variant="filled-red"
                className="chosen-bank-info__action"
                onClick={() => setShowCancelModal(true)}
                disabled={!canCancelOffer}
              >
                <CrossIcon className="chosen-bank-info__cross-icon" />
                <span className="chosen-bank-info__action-hidden">
                  Отменить предложение
                </span>
              </Button>
            )}

          <BankExternalQr />
        </div>
      </div>

      <CustomModal
        title="Текущий статус"
        isModalOpen={description !== ''}
        handleOk={() => setDescription('')}
        handleCancel={() => setDescription('')}
        editClass={''}
        footer={[]}
      >
        <pre className="chosen-bank-info__description">{description}</pre>
      </CustomModal>

      <CancelModal
        isOpened={showCancelModal}
        onClose={() => setShowCancelModal(false)}
      />

      <RefundModal
        isOpened={showRefundModal}
        onClose={() => setShowRefundModal(false)}
      />
    </>
  );
};

export default ChosenBankInfo;
